import React, { useState, useContext, useEffect } from 'react'
import ResponsiveAppBar from '../../components/Navbar'
import { create } from '../../services/customer'
import { UserContext } from '../../context/UserProvider.jsx'
import { useNavigate } from 'react-router-dom'
import {
  TextField,
  Button,
  Grid,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import Select from 'react-select'
import { PaymentOptionFields } from './PaymentInformation'
import { canManagePaymentOptions } from '../../services/customerPaymentOptions'
import { getSalesPeople } from '../../services/admin'
const Form = ({ onSubmit, saveError, saving }) => {
  const { token, access } = useContext(UserContext)
  const isCustomerOnboarder = access?.role === 'customer_onboarder'
  const isCustomerOnboardingUser = isCustomerOnboarder || access?.control?.['customer onboarder'] === true
  const canSelectSalesPerson = !isCustomerOnboardingUser || access?.control?.['sales person'] === true
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    companyName: '',
    accountNumber: '',
    addressLine1: '',
    addressLine2: '',
    emailAddress: '',
    phoneNumber: '',
    password: '',
    salesPerson: null,
    paymentOption: { method: '', reference: '', expiration_date: '', label: '' },
  })
  const [salesPersonList, setSalesPersonList] = useState([])

  useEffect(() => {
    if (!canSelectSalesPerson) return
    getSalesPeople(token).then((res) => setSalesPersonList(res.collection ?? []))
  }, [])

  const handleGeneratePassword = () => {
    const randomPassword = Math.random().toString(36).slice(-8)
    setFormData((prevFormData) => ({
      ...prevFormData,
      password: randomPassword,
    }))
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }))
  }

  const handleSelectChange = (event) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      salesPerson: event,
    }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    onSubmit(formData)
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword)
  }

  return (
    <Paper elevation={0} sx={{ padding: 3 }}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {canManagePaymentOptions(access) && <Grid item xs={12}><PaymentOptionFields optional value={formData.paymentOption} onChange={paymentOption => setFormData(previous => ({ ...previous, paymentOption }))} /></Grid>}
          {canSelectSalesPerson && <Grid item xs={12}>
            <Select
              required
              options={salesPersonList}
              onChange={handleSelectChange}
              placeholder="Sales Person *"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  background: '#fff',
                  borderColor: '#9e9e9e',
                  height: '55px',
                  boxShadow: state.isFocused ? null : null,
                }),

                valueContainer: (provided, state) => ({
                  ...provided,
                  height: '55px',
                  padding: '0 6px',
                }),

                input: (provided, state) => ({
                  ...provided,
                  margin: '0px',
                }),
                indicatorSeparator: (state) => ({
                  display: 'none',
                }),
                indicatorsContainer: (provided, state) => ({
                  ...provided,
                  height: '55px',
                }),
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </Grid>}

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Account Number"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Address Line 1"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              name="addressLine2"
              value={formData.addressLine2}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Email Address"
              name="emailAddress"
              type="email"
              value={formData.emailAddress}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={handleTogglePasswordVisibility}
                      type="button"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="button" variant="contained" onClick={handleGeneratePassword}>
              Generate Password
            </Button>
          </Grid>
          {saveError && (
            <Grid item xs={12}>
              <Alert severity="error" role="alert">
                {saveError}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              color="primary"
              disabled={saving}
              startIcon={saving ? <CircularProgress color="inherit" size={18} /> : null}
              type="submit"
              variant="contained"
            >
              {saving ? 'Creating customer…' : 'Create customer'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  )
}

const App = () => {
  const navigate = useNavigate()
  const { token } = useContext(UserContext)
  const [saveError, setSaveError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (payload) => {
    setSaveError('')
    setSaving(true)
    const res = await create(token, {
      'company-name': payload.companyName,
      'account-number': payload.accountNumber,
      'address-line-1': payload.addressLine1,
      'address-line-2': payload.addressLine2,
      email: payload.emailAddress,
      phone: payload.phoneNumber,
      password: payload.password,
      'sales-person': payload.salesPerson,
      ...(payload.paymentOption?.method ? { 'payment-option': { method: payload.paymentOption.method, ...(payload.paymentOption.method === 'Credit Card' ? { reference: payload.paymentOption.reference, expiration_date: payload.paymentOption.expiration_date, label: payload.paymentOption.label } : {}) } } : {}),
    })
    setSaving(false)

    if (res?.status) {
      const validationMessage = Object.values(res.data?.errors ?? {})
        .flat()
        .join(' ')
      setSaveError(
        validationMessage || res.data?.message || 'The customer could not be created.'
      )
      return
    }

    alert('Customer created successfully')
    navigate('/customers')
  }

  return (
    <>
      <ResponsiveAppBar />
      <Container maxWidth={'xl'}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <h1>Create Customers</h1>
          </Grid>

          <Grid item xs={8}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                navigate('/customers')
              }}
              style={{
                marginTop: '20px',
                backgroundColor: '#3675af',
                color: 'white',
                height: '55px',
                float: 'right',
              }}
            >
              Back
            </Button>
          </Grid>
        </Grid>
        <div
          style={{
            minHeight: '80vh',
            width: '100%',
            backgroundColor: '#ffff',
            marginTop: '20px',
          }}
        >
          <Form
            onSubmit={handleSubmit}
            saveError={saveError}
            saving={saving}
          />
        </div>
      </Container>
    </>
  )
}

export default App
